import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Link, Typography, TextField } from '@mui/material'

import { makeFormPropTypes } from '../../../common/formikPropTypes'
import { googleAddressPropTypes } from '../../../common/propTypes'
import { getFieldProps } from '../../../common/formProps'
import { ADDRESS_TYPE_CODE } from '../../../common/constants'
import useTranslations from '../../../i18n/useTranslations'
import { getGPlacesRestrictionsForAddressType } from '../../../context/business'
import GPlacesAutocomplete from '../../../ui/modules/GPlacesAutocomplete'
import Checkbox from '../../../ui/components/checkbox'

import styles from './styles'

const AddressEntry = ({
  isHidden = true,
  title,
  onChangeAutocompleteAddress,
  onChangeAutocompleteInput,
  autocompleteErrors,
  values,
  fieldErrors,
  formErrors,
  touched,
  onChange,
  onBlur,
  onSubmit,
  addressType,
  openAgenda,
  formik,
  isAddressConfirmed,
  isDestination,
}) => {
  const t = useTranslations()
  const countryRestrictions = getGPlacesRestrictionsForAddressType(addressType)

  const isSubmitBtnDisabled =
    formErrors?.length !== 0 ||
    Object.entries(fieldErrors)?.length !== 0 ||
    autocompleteErrors?.length !== 0

  const getMissingFieldsStr = fields => {
    const translatedFields = fields.map(field => t(`noun:article:${field}`))
    if (translatedFields.length === 1) return translatedFields[0]
    const last = translatedFields.pop()
    return translatedFields.join(', ').concat(t('noun:and'), last)
  }

  useEffect(() => {
    formik.setValues(formik.initialValues)
  }, [])

  return (
    <Box
      sx={{
        ...styles.addressEntry,
        ...(isHidden ? styles.addressEntryHidden : {}),
        ...(isAddressConfirmed ? null : styles.addressEntryNotConfirmed),
      }}
    >
      <form noValidate onSubmit={onSubmit}>
        <Box sx={styles.addressEntryTitle}>
          <Typography variant="h2">{title}</Typography>
          <Link component="button" onClick={openAgenda}>
            {t('action:choose-direction-from-agenda')}
          </Link>
        </Box>

        <TextField
          name="name"
          id="name"
          label={t('noun:name-company')}
          type="text"
          {...getFieldProps('name', values, fieldErrors, touched, t)}
          onChange={onChange}
          onBlur={onBlur}
          className="input-full"
          sx={styles.textField}
          InputLabelProps={{ required: true }}
        />

        <TextField
          name="phone"
          id="phone"
          label={t('noun:mobile-phone')}
          type="text"
          {...getFieldProps('phone', values, fieldErrors, touched, t)}
          onChange={onChange}
          onBlur={onBlur}
          className="input-full"
          sx={styles.textField}
          InputLabelProps={{ required: true }}
        />

        <TextField
          name="email"
          id="email"
          label={t('noun:email')}
          type="email"
          {...getFieldProps('email', values, fieldErrors, touched, t)}
          onChange={onChange}
          onBlur={onBlur}
          className="input-full"
          sx={styles.textField}
          InputLabelProps={{ required: false }}
        />

        <GPlacesAutocomplete
          onChangeAddress={onChangeAutocompleteAddress}
          onChangeInput={onChangeAutocompleteInput}
          sx={styles.textField}
          error={
            autocompleteErrors?.length !== 0
              ? t('error:field:address-autocomplete', {
                  missingFields: getMissingFieldsStr(autocompleteErrors),
                })
              : ''
          }
          countryRestrictions={countryRestrictions}
          isRequired
        />

        <Box sx={styles.addressEntryDestinationExtraInfoWrapper}>
          <TextField
            name="stair"
            id="stair"
            label={t('noun:stair')}
            type="text"
            {...getFieldProps('stair', values, fieldErrors, touched, t)}
            onChange={onChange}
            onBlur={onBlur}
            className="input-full"
          />

          <TextField
            name="floor"
            id="floor"
            label={t('noun:floor')}
            type="text"
            {...getFieldProps('floor', values, fieldErrors, touched, t)}
            onChange={onChange}
            onBlur={onBlur}
            className="input-full"
          />

          <TextField
            name="letter"
            id="letter"
            label={t('noun:letter')}
            type="text"
            {...getFieldProps('letter', values, fieldErrors, touched, t)}
            onChange={onChange}
            onBlur={onBlur}
            className="input-full"
          />
        </Box>

        <Checkbox
          name="saveAddress"
          label={t('action:save-address-my-agenda')}
          checked={values.saveAddress}
          sx={styles.saveNewAddressCheckbox}
          onChange={onChange}
        />

        <Button
          fullWidth
          variant="outlined"
          type="submit"
          disabled={isSubmitBtnDisabled}
          sx={styles.addressEntryConfirmAddressButton}
        >
          {t('shipping-send-form:action:confirm-data-destination')}
        </Button>
      </form>
    </Box>
  )
}

AddressEntry.propTypes = {
  isHidden: PropTypes.bool,
  title: PropTypes.string,
  onChangeAutocompleteAddress: PropTypes.func,
  onChangeAutocompleteInput: PropTypes.func,
  autocompleteErrors: PropTypes.arrayOf(PropTypes.string),
  showSubmitButton: PropTypes.bool,
  ...makeFormPropTypes(googleAddressPropTypes),
  addressType: PropTypes.oneOf(Object.values(ADDRESS_TYPE_CODE)),
  formik: PropTypes.object.isRequired,
  isAddressConfirmed: PropTypes.bool,
  isDestination: PropTypes.bool,
}

export default AddressEntry
